import { useOutletContext } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  createFilterOptions,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  InputAdornment,
} from "@mui/material";
import useStyles from "./styles";
import {
  Add,
  ArrowDropDownOutlined,
  Check,
  CheckBox,
  CheckBoxOutlineBlank,
  Clear,
  Delete,
  DeleteOutline,
  MoreHoriz,
  Search,
  StorageOutlined,
} from "@mui/icons-material";
import { TAG_COLORS } from "../interfaceTags";
import DatagridComponent from "../../../../../../components/datagridComponent";
import { useState } from "react";


function LinkSelector({
  url = "",
  type = "",
  title = "",
  icon = null,
  loading = {},
  editMode = false,
  interfaceList = {},
  blockedHostIds = [],
  manipulateCpes = [],
  availableHosts = [],
  availableDevices = [],
  getInterfaces = () => { },
  setManipulateCpes = () => { },
  interfaceTags = [],
  onCreateTag = async () => null,
  onEditTag = () => { },
  onDeleteTag = () => { },
}) {
  // Rótulo do serviço do cliente aberto ("Serviço"/"Contrato").
  const { labelErp: LABELERP = "Serviço" } = useOutletContext() || {};
  const theme = useTheme();
  const styles = useStyles(theme);
  const filter = createFilterOptions();

  const checkboxIcon = <CheckBoxOutlineBlank fontSize="small" />;
  const checkboxCheckedIcon = <CheckBox fontSize="small" />;

  const COLOR_NAMES = ["Vermelho", "Verde", "Laranja", "Azul", "Roxo", "Cinza"];

  const [searchTerms, setSearchTerms] = useState({});

  // Menu de tags (igual ao anchorElOptionsGrupo do blocosEasyFlow)
  const [anchorElTagsMenu, setAnchorElTagsMenu] = useState(null);
  const [tagMenuProps, setTagMenuProps] = useState(null); // { index, ifaceName }
  const [buscarTag, setBuscarTag] = useState("");

  // Menu de edição de tag (igual ao anchorEl/handleGrupo do blocosEasyFlow)
  const [anchorElEditTag, setAnchorElEditTag] = useState(null);
  const [handleTag, setHandleTag] = useState({ id: "", tagName: "", colorTag: "" });

  const openTagsMenu = Boolean(anchorElTagsMenu);
  const openEditTag = Boolean(anchorElEditTag);

  const handleSearchChange = (index, value) => {
    setSearchTerms((prev) => ({ ...prev, [index]: value }));
  };

  const handleClickTagsMenu = (event, props) => {
    setAnchorElTagsMenu(event.currentTarget);
    setTagMenuProps(props);
  };

  const handleCloseTagsMenu = () => {
    setAnchorElTagsMenu(null);
    setBuscarTag("");
  };

  const handleCloseEditTag = () => {
    setAnchorElEditTag(null);
    setHandleTag({ id: "", tagName: "", colorTag: "" });
  };

  // Resolve tags atribuídas a partir das definições atuais
  const getResolvedTags = (tags) => {
    return (tags || []).map((t) => interfaceTags.find((d) => d?.id === t?.id)).filter(Boolean);
  };

  // Filtra tags disponíveis pela busca
  const getFilteredTags = () => {
    const term = (buscarTag || "").trim().toLowerCase();
    return interfaceTags.filter((t) => (t?.tagName || "").toLowerCase().includes(term));
  };

  const handleDatagridSelectionChange = (index, currentSelectionIds, sortedAndFilteredRows) => {
    const newCpes = [...(manipulateCpes ?? [])];
    const cpe = newCpes[index];

    const previouslySelected = cpe?.hostsClienteInterfaces || [];
    const visibleIds = sortedAndFilteredRows.map((r) => r.id);

    const itemsNotVisible = previouslySelected.filter(
      (item) => !visibleIds.includes(getInterfaceName(item))
    );

    const itemsNewlySelectedNames = currentSelectionIds
      ?.filter((id) => visibleIds.includes(id)) || [];

    const prevTagsByName = new Map(
      previouslySelected.map((i) => [getInterfaceName(i), i?.tags ?? []])
    );

    const itemsNewlySelected = itemsNewlySelectedNames.map((name) => {
      return { interface: name, tags: prevTagsByName.get(name) ?? [] };
    });

    newCpes[index] = {
      ...cpe,
      hostsClienteInterfaces: [
        ...itemsNotVisible,
        ...itemsNewlySelected
      ]
    };

    setManipulateCpes?.(newCpes);
  };

  /** ADICIONAR / REMOVER CPE */
  const handleAddCpe = () => {
    const newCpe = {
      groupId: "",
    };

    setManipulateCpes?.([...(manipulateCpes ?? []), newCpe]);
  };

  const handleRemoveCpe = (index) => {
    const newCpes = [...(manipulateCpes ?? [])];
    newCpes.splice(index, 1);
    setManipulateCpes?.(newCpes);
  };

  /** OBTER OPÇÕES */
  const getAvailableModels = (currentIndex) => {
    return availableDevices ?? [];

    // VERIFICAR SE O modelo JA FOI SELECIONADO
    const selectedGroupIds = manipulateCpes
      ?.filter((_, idx) => idx !== currentIndex)
      ?.map((onu) => onu?.groupId)
      ?.filter(Boolean);

    return availableDevices?.filter(
      (device) => !selectedGroupIds?.includes(device?.groupid),
    );
  };

  const getAvailableHosts = (currentIndex, selectedGroupId) => {
    if (!selectedGroupId) return [];

    // Hosts já selecionados em outros CPEs
    const usedHostIds = manipulateCpes
      ?.filter((_, idx) => idx !== currentIndex)
      ?.map((cpe) => cpe?.hostid || cpe?.hostId)
      ?.filter(Boolean);

    return (
      availableHosts?.filter((host) => {
        const hostId = host?.hostid || host?.hostId;

        const belongsToGroup = host?.groups?.some(
          (group) => group?.groupid === selectedGroupId,
        );

        const notBlocked = !blockedHostIds?.some((b) => b?.hostid === hostId);

        const notUsed = !usedHostIds.includes(hostId);

        return belongsToGroup && notBlocked && notUsed;
      }) ?? []
    );
  };

  // const getAvailableHosts = (currentIndex, selectedGroupId) => {
  //   if (!selectedGroupId) return [];

  //   const hasSameGroup = availableHosts?.filter((host) =>
  //     host?.groups?.some(
  //       (group) =>
  //         group?.groupid === selectedGroupId &&
  //         !blockedHostIds.includes(host?.hostid)
  //     )
  //   );
  //   return hasSameGroup;
  // };

  const getAvailableInterfaces = (cpe) => {
    const hostId = cpe?.hostid || cpe?.hostId;

    if (!hostId) return [];

    return interfaceList?.[type]?.[hostId] ?? [];
  };



  /** SELECIONAR OPÇÕES */
  const handleModelSelect = (index, value) => {
    const newCpes = [...(manipulateCpes ?? [])];

    newCpes[index] = {
      groupId: value?.groupid || "",
    };

    setManipulateCpes?.(newCpes);
  };

  const handleHostSelect = (index, newValues) => {
    const newCpes = [...(manipulateCpes ?? [])];
    const selectedHost = newValues?.[0];

    newCpes[index] = {
      ...(newCpes[index] ?? {}),
      ...(selectedHost ?? {}),
      hostsClienteInterfaces: [],
    };

    if (selectedHost) {
      getInterfaces?.({ hostId: selectedHost?.hostid, type: type, url: url });
    }
    setManipulateCpes?.(newCpes);
  };

  /** Extrai o valor string (name) de uma interface.
   *  Objetos persistidos têm `.interface`; opções ao vivo do Zabbix têm `.name`. */
  const getInterfaceName = (iface) => {
    if (!iface) return "";
    if (typeof iface === "string") return iface;
    return iface?.interface ?? iface?.name ?? "";
  };

  /** Monta o label de exibição: "name - description" ou só "name" */
  const getInterfaceLabel = (iface) => {
    if (!iface) return "";
    if (typeof iface === "string") return iface;
    const name = iface?.interface ?? iface?.name ?? "";
    const desc = iface?.description;
    return desc ? `${name} - ${desc}` : name;
  };



  /** Atribuição de tags por interface (muta apenas o estado local).
   *  Se a interface ainda não estiver em hostsClienteInterfaces, ela é
   *  automaticamente incluída (selecionada) quando a primeira tag for adicionada. */
  const updateInterfaceTags = (cpeIndex, ifaceName, updater) => {
    const newCpes = [...(manipulateCpes ?? [])];
    const cpe = newCpes[cpeIndex];
    if (!cpe) return;

    const existing = cpe.hostsClienteInterfaces ?? [];
    const alreadyIn = existing.some((i) => getInterfaceName(i) === ifaceName);

    const updatedList = alreadyIn
      ? existing.map((i) =>
          getInterfaceName(i) === ifaceName
            ? { interface: ifaceName, tags: updater(i?.tags ?? []) }
            : i,
        )
      : [...existing, { interface: ifaceName, tags: updater([]) }];

    newCpes[cpeIndex] = {
      ...cpe,
      hostsClienteInterfaces: updatedList,
    };

    setManipulateCpes?.(newCpes);
  };

  const handleAssignTag = (cpeIndex, ifaceName, tag) =>
    updateInterfaceTags(cpeIndex, ifaceName, (tags) =>
      tags.some((t) => t?.id === tag?.id) ? tags : [...tags, tag],
    );

  const handleUnassignTag = (cpeIndex, ifaceName, tag) =>
    updateInterfaceTags(cpeIndex, ifaceName, (tags) =>
      tags.filter((t) => t?.id !== tag?.id),
    );

  return (
    <Box sx={styles?.container}>
      <Box sx={styles?.boxTitle}>
        <> {icon}</>
        <Typography variant="mySubtitle2">
          {title || "Configurações"}
        </Typography>
      </Box>
      <Box sx={styles.boxMapOnus}>
        {manipulateCpes?.map?.((cpe, index) => {
          // Models
          const availableModels = getAvailableModels(index);
          const currentModel = availableDevices?.find(
            (d) => d?.groupid === cpe?.groupId,
          );
          // Hosts
          const availableHostsForModel =
            getAvailableHosts?.(index, cpe?.groupId) ?? [];
          const hostOptions = availableHostsForModel ?? [];
          const currentHost = availableHostsForModel.find(
            (h) => h.hostid === cpe?.hostId || h.hostid === cpe?.hostid,
          );
          // Interfaces
          const availableInterfaces = getAvailableInterfaces(cpe);
          const selected = cpe?.hostsClienteInterfaces ?? [];
          const selectedInterfaceNames = selected.map(getInterfaceName);

          const searchTerm = searchTerms[index] || "";

          let sortedAndFilteredRows = availableInterfaces.map(iface => {
            const name = getInterfaceName(iface);
            const label = getInterfaceLabel(iface);
            return { id: name, name, label, original: iface };
          });

          if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            sortedAndFilteredRows = sortedAndFilteredRows.filter(r => r.name.toLowerCase().includes(lowerSearch) || r.label.toLowerCase().includes(lowerSearch));
          }

          const selectedIdsSet = new Set(selectedInterfaceNames);
          sortedAndFilteredRows.sort((a, b) => {
            const aSel = selectedIdsSet.has(a.id);
            const bSel = selectedIdsSet.has(b.id);
            if (aSel && !bSel) return -1;
            if (!aSel && bSel) return 1;
            return 0;
          });

          const columns = [
            {
              field: "label",
              headerName: "Interface",
              flex: 2,
            },
            {
              field: "tags",
              headerName: "Tags",
              flex: 1,
              sortable: false,
              renderCell: (params) => {
                const ifaceName = params.row.name;
                const currentIface = selected.find(i => getInterfaceName(i) === ifaceName);
                const tags = currentIface?.tags ?? [];
                const resolvedTags = getResolvedTags(tags);

                const primeiraTag = resolvedTags?.[0];
                const totalTags = resolvedTags?.length || 0;
                const tagsRestantes = totalTags > 1 ? totalTags - 1 : 0;

                return (
                  <Button
                    size="small"
                    onClick={(event) => {
                      handleClickTagsMenu(event, { index, ifaceName });
                    }}
                    disabled={!editMode}
                    sx={{
                      ...styles.buttonTag,
                      justifyContent: "flex-start",
                    }}
                    variant="text"
                    endIcon={<ArrowDropDownOutlined sx={{ fontSize: 18 }} />}
                  >
                    <Box
                      sx={{
                        ...styles.containerDatagridTags,
                      }}
                    >
                      {totalTags > 0 ? (
                        <>
                          {/* Primeira tag */}
                          <Chip
                            size="small"
                            label={
                              <Typography
                                sx={{ fontWeight: "bold" }}
                                variant="caption"
                                noWrap
                              >
                                {primeiraTag?.tagName}
                              </Typography>
                            }
                            sx={{
                              ...styles.chipTag,
                              height: 18,
                              maxWidth: "100%",
                              bgcolor: primeiraTag?.colorTag,
                              flex: 1,
                            }}
                          />

                          {/* Contador +X */}
                          {tagsRestantes > 0 && (
                            <Chip
                              size="small"
                              label={`+${tagsRestantes}`}
                              sx={styles.chipCountTag}
                            />
                          )}
                        </>
                      ) : (
                        <Typography
                          variant="caption"
                          noWrap
                          sx={{
                            ...styles.textNoTag,
                            fontSize: "0.63rem",
                          }}
                        >
                          Adicione ou crie uma tag
                        </Typography>
                      )}
                    </Box>
                  </Button>
                );
              }
            }
          ];

          return (
            <Box>
              <Grid container spacing={1} key={cpe?.groupId || index}>
                <Grid item xs={11.6}>
                  <Paper
                    variant="outlined"
                    elevation={0}
                    sx={styles.paperOnuItem}
                  >
                    <Grid container spacing={1}>
                      {/* Modelo */}
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          size="small"
                          options={availableModels ?? []}
                          value={currentModel ?? null}
                          getOptionLabel={(option) => option?.name ?? ""}
                          isOptionEqualToValue={(option, value) =>
                            option?.groupid === value?.groupid
                          }
                          onChange={(event, newValue) =>
                            handleModelSelect?.(index, newValue)
                          }
                          renderInput={(params) => (
                            <TextField
                              variant="filled"
                              {...params}
                              label="Modelo"
                              placeholder="Selecione o modelo"
                            />
                          )}
                          disabled={
                            (availableModels?.length ?? 0) === 0 || !editMode
                          }
                        />
                      </Grid>
                      {/* Host */}
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          size="small"
                          options={
                            availableHostsForModel?.length > 0
                              ? hostOptions.filter((opt) => !opt.isSelectAll)
                              : []
                          }
                          value={currentHost}
                          onChange={(event, newValue) => {
                            // seleção única
                            handleHostSelect(index, newValue ? [newValue] : []);
                          }}
                          getOptionLabel={(option) => option?.host ?? ""}
                          isOptionEqualToValue={(option, value) => {
                            const optionId = option.hostid || option.hostId;
                            const valueId = value?.hostid || value?.hostId;
                            return optionId === valueId;
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="filled"
                              label="Hosts"
                              placeholder="Selecione um host"
                            />
                          )}
                          renderOption={(props, option) => {
                            // Bloqueado
                            const blockedInfo = blockedHostIds.find(
                              (b) => b.hostid === option.hostid,
                            );
                            const isBlocked = Boolean(blockedInfo);

                            const optionLi = (
                              <li
                                {...props}
                                style={{
                                  opacity: isBlocked ? 0.5 : 1,
                                  pointerEvents: isBlocked ? "none" : "auto",
                                }}
                              >
                                {option.host}
                              </li>
                            );

                            // Tooltip se estiver bloqueado
                            return isBlocked ? (
                              <Tooltip
                                title={`Já selecionado no ${LABELERP}: ${blockedInfo?.servico}`}
                                placement="top"
                              >
                                <div style={{ pointerEvents: "auto" }}>
                                  {optionLi}
                                </div>
                              </Tooltip>
                            ) : (
                              optionLi
                            );
                          }}
                          disabled={
                            !cpe?.groupId ||
                            (availableHostsForModel?.length ?? 0) === 0 ||
                            !editMode
                          }
                        />
                      </Grid>
                      {/* Interfaces */}
                      <Grid item xs={12}>
                        <Box position={"relative"} width={"100%"}>
                          {!!loading.linksAndSessions ||
                            (!!loading[type] && (
                              <LinearProgress sx={styles.linearProgressOnu} />
                            ))}
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 2 }}>
                              <Box>
                                <Typography sx={{ fontWeight: "bold" }}>
                                  Interfaces
                                </Typography>
                                <Typography sx={{ fontSize: "12px", opacity: 0.5 }}>
                                  Selecione as interfaces e gerencie suas tags.
                                </Typography>
                              </Box>

                              <TextField
                                size="small"
                                variant="outlined"
                                placeholder="Buscar interface..."
                                value={searchTerm}
                                onChange={(e) => handleSearchChange(index, e.target.value)}
                                sx={{ minWidth: "300px" }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Search fontSize="small" />
                                    </InputAdornment>
                                  ),
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      {searchTerm && (
                                        <IconButton size="small" onClick={() => handleSearchChange(index, "")}>
                                          <Clear fontSize="small" />
                                        </IconButton>
                                      )}
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </Box>

                            <Paper variant="outlined" sx={{ height: 400, width: "100%" }}>
                              <DatagridComponent
                                options={{
                                  checkboxSelection: true,
                                  onRowSelectionModelChange: (currentSelectionIds) => {
                                    handleDatagridSelectionChange(index, currentSelectionIds, sortedAndFilteredRows);
                                  },
                                  rowSelectionModel: selectedInterfaceNames,
                                  disableRowSelectionOnClick: true,
                                  isRowSelectable: () => editMode,
                                }}
                                rows={sortedAndFilteredRows}
                                columns={columns}
                              />
                            </Paper>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                <Grid item xs={0.4} sx={styles?.deleteIcon}>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveCpe?.(index)}
                    disabled={!editMode}
                  >
                    <DeleteOutline color={editMode ? "error" : "disabled"} />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>
          );
        })}
        <Button
          variant="outlined"
          size="small"
          startIcon={<Add color={theme.palette.text.main} />}
          onClick={handleAddCpe}
          disabled={!editMode || !!Object.values(loading)?.some(Boolean)}
          sx={styles.button}
        >
          Adicionar {title}
        </Button>
      </Box>

      {/* Menu de edição de tag (igual ao Menu de edição de grupo do blocosEasyFlow) */}
      <Menu
        anchorEl={anchorElEditTag}
        open={openEditTag}
        onClose={handleCloseEditTag}
        transformOrigin={{ horizontal: "center", vertical: "top" }}
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
      >
        <Box>
          <TextField
            value={handleTag?.tagName}
            size="small"
            sx={{ margin: "10px" }}
            onChange={(e) => {
              e.stopPropagation();
              setHandleTag((prev) => ({
                ...prev,
                tagName: e.target.value,
              }));
            }}
            onKeyDown={(e) => e.stopPropagation()}
            error={!handleTag?.tagName?.trim()}
            helperText={
              !handleTag?.tagName?.trim() && "Nome obrigatório"
            }
          />
        </Box>
        <Box>
          <Divider />
          <Button
            startIcon={<Delete color="error" sx={{ marginLeft: "25px" }} />}
            sx={styles.buttonDeleteTag}
            onClick={() => {
              onDeleteTag(handleTag?.id);
              handleCloseEditTag();
              if (tagMenuProps) {
                handleUnassignTag(tagMenuProps.index, tagMenuProps.ifaceName, handleTag);
              }
            }}
          >
            Deletar tag
          </Button>
          <Divider />
        </Box>
        <Box sx={{ p: 1 }}>
          <Typography sx={styles.textNoTag}>Cor</Typography>
        </Box>

        <Box>
          {TAG_COLORS?.map((color, idx) => (
            <MenuItem
              key={color}
              onClick={() =>
                setHandleTag((prev) => ({
                  ...prev,
                  colorTag: color,
                }))
              }
            >
              <Box
                sx={{
                  ...styles.colorSelectChip,
                  backgroundColor: color,
                }}
              />
              <Typography>
                {COLOR_NAMES[idx]}
              </Typography>
              {handleTag.colorTag === color && (
                <ListItemIcon sx={{ marginLeft: "auto" }}>
                  <Check />
                </ListItemIcon>
              )}
            </MenuItem>
          ))}
        </Box>
        <Box sx={styles.containerHandleButtons}>
          <Button onClick={handleCloseEditTag} size="small">
            Cancelar
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={!handleTag?.tagName?.trim()}
            onClick={() => {
              onEditTag(handleTag);
              handleCloseEditTag();
            }}
          >
            Salvar
          </Button>
        </Box>
      </Menu>

      {/* Menu de opções de tag (igual ao Menu de opções de grupo do blocosEasyFlow) */}
      <Menu
        anchorEl={anchorElTagsMenu}
        open={openTagsMenu}
        onClose={() => {
          handleCloseTagsMenu();
          setBuscarTag("");
        }}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        sx={{ width: "450px" }}
      >
        {tagMenuProps && (() => {
          const cpeIndex = tagMenuProps.index;
          const ifaceName = tagMenuProps.ifaceName;
          const cpe = manipulateCpes?.[cpeIndex];
          const currentIface = (cpe?.hostsClienteInterfaces ?? []).find(i => getInterfaceName(i) === ifaceName);
          const tags = currentIface?.tags ?? [];
          const resolvedTags = getResolvedTags(tags);
          const assignedIds = new Set(tags.map((t) => t?.id));
          const filteredTags = getFilteredTags();
          const term = (buscarTag || "").trim();

          return (
            <>
              <Autocomplete
                freeSolo
                size="small"
                inputValue={buscarTag}
                onInputChange={(event, value) => setBuscarTag(value)}
                clearIcon={null}
                filterOptions={(options, params) => {
                  const filtered = filter(options, params);
                  const { inputValue } = params;
                  setBuscarTag(inputValue);
                  return [];
                }}
                disableCloseOnSelect
                multiple
                onKeyDown={(event) => {
                  event.stopPropagation();
                  if (
                    event.key === "Enter" &&
                    term &&
                    (!filteredTags?.some(
                      (f) => f?.tagName === term,
                    ) ||
                      filteredTags?.length === 0)
                  ) {
                    onCreateTag(term).then((created) => {
                      if (created) {
                        handleAssignTag(cpeIndex, ifaceName, created);
                      }
                    });
                  }
                }}
                options={[]}
                value={resolvedTags || []}
                getOptionLabel={(option) => option?.tagName}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, idx) => {
                    const { key, onDelete, ...tagProps } = getTagProps({
                      index: idx,
                    });

                    return (
                      <Chip
                        sx={{ ...styles.chipRenderTag, bgcolor: option?.colorTag }}
                        key={key}
                        label={option.tagName}
                        {...tagProps}
                        onDelete={() => {
                          handleUnassignTag(cpeIndex, ifaceName, option);
                        }}
                      />
                    );
                  })
                }
                style={{ width: 280, margin: "0 auto" }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      resolvedTags?.length !== 0 ? "" : "Adicione ou crie uma tag"
                    }
                  />
                )}
              />

              <Box>
                <Divider sx={{ margin: "10px 0" }} />
                <Box sx={{ ml: "10px" }}>
                  <Typography sx={styles.textNoTag}>
                    Selecione ou crie uma opção{" "}
                  </Typography>
                </Box>
              </Box>
              <Box sx={styles.containerTagsList}>
                {filteredTags?.length === 0 && buscarTag === "" && (
                  <Box sx={styles.boxInfoTag}>
                    <Typography sx={styles.textInfoTag}>
                      Nenhuma tag criada
                    </Typography>
                    <Typography sx={styles.textInfoTag}>
                      Digite para criar uma tag
                    </Typography>
                  </Box>
                )}

                {filteredTags?.map((tag) => (
                  <Box key={tag.id} style={{ width: 300 }}>
                    <MenuItem
                      onClick={(e) => {
                        if (!assignedIds.has(tag?.id)) {
                          handleAssignTag(cpeIndex, ifaceName, tag);
                        } else {
                          handleUnassignTag(cpeIndex, ifaceName, tag);
                        }
                        e.stopPropagation();
                      }}
                    >
                      <Checkbox
                        icon={checkboxIcon}
                        checkedIcon={checkboxCheckedIcon}
                        checked={assignedIds.has(tag?.id)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ marginRight: 8 }}
                        onChange={(e) => {
                          e.stopPropagation();
                          if (e.target.checked) {
                            handleAssignTag(cpeIndex, ifaceName, tag);
                          } else {
                            handleUnassignTag(cpeIndex, ifaceName, tag);
                          }
                        }}
                      />
                      <Chip
                        size="small"
                        label={tag?.tagName}
                        sx={{
                          ...styles.chipTag,
                          bgcolor: tag?.colorTag,
                        }}
                      />

                      <IconButton
                        onKeyDown={(event) => event.stopPropagation()}
                        onClick={(event) => {
                          setAnchorElEditTag(event.currentTarget);
                          setHandleTag({ ...tag });
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                        sx={{ marginLeft: "auto" }}
                      >
                        <MoreHoriz fontSize="small" />
                      </IconButton>
                    </MenuItem>
                  </Box>
                ))}
              </Box>
              {term &&
                (!filteredTags?.some((f) => f?.tagName === term) ||
                  filteredTags?.length === 0) && (
                  <MenuItem
                    style={{ width: 300 }}
                    onClick={(e) => {
                      onCreateTag(term).then((created) => {
                        if (created) {
                          handleAssignTag(cpeIndex, ifaceName, created);
                        }
                      });
                      e.stopPropagation();
                    }}
                  >
                    <Typography style={{ marginLeft: "20px" }}>
                      Criar "{term}"
                    </Typography>
                  </MenuItem>
                )}
            </>
          );
        })()}
      </Menu>
    </Box>
  );
}

export default LinkSelector;
